import { createSlice } from '@reduxjs/toolkit';
import { fetchWithToken, postWithToken, patchWithToken, deleteWithToken, postRequest, patchRequest } from '../../config/apiRequests';
import { showSuccessToast, showErrorToast } from '../../utils/toastUtils';
import { handleApiResponse } from "../../utils/reducerHelpers"; // must exist

// Helper for simple fetch reducers
const setFetchState = (state, action, { endpoint = "", destKey = null, successMessage = "" } = {}) => {
  state.endPoint = endpoint;
  state.isLoading = false;

  if (action.payload) {
    state.success = true;
    if (successMessage) state.message = successMessage;
    if (destKey) {
      state[destKey] = action.payload;
    }
  } else {
    state.success = false;
  }
};

export const companyDashboardSlice = createSlice({
  name: 'companyDashboard',
  initialState: {
    // Core Status
    isLoading: false,
    success: false,
    message: '',
    endPoint: '',

    setupServiceData: {},

    // Services
    servicesData: {},
    newServicesData: {},
    authorizedServicesData: {},
    CategoriesAndUsersData: {},
    serviceCreated: false,
    serviceUpdated: false,
    serviceMenuUpdated: false,

    // Employees
    allEmployees: {},
    employeesData: undefined,
    managerName: '',
    employeeData: undefined,
    userMenuUpdated: false,

    // Service Members
    serviceMemberData: {},
    serviceMemberCreated: false,
    serviceMemberUpdated: false,

    // Integrations
    newIntegrationData: {},
    allIntegrationData: {},
    serviceIntegrations: {},
    integrationCreated: false,
    integrationUpdated: false,
    deletedIntegration: {},

    // Contracts
    contractData: {},
    ServiceContractData: {},
    createContract: false,

    // Usage & Reports
    homeData: {},
    usageReportData: {},
    printingUsageReportData: {},
    submissionsData: {},
    submissionsRecord: [],
    SubmissionsListData: [],
    usageReportFeedbacksData: {},
    specficFeedbackData: {},
    usageReportRequestData: {},
    usageReportServiceData: {},
    YearlyUsegeServiceData: {},
    companyExpenses: [],
    monthReportData: {},
    publishCompanyReport: {},
    publishCompanyReportTime: {},
    customerPublishData: {},

    // Employee-Specific
    employeeRequestData: {},
    employeeSubmissionData: {},
    empSubmissionDetail: {},
    employeefeedbackData: {},
    employeefeedbacksingle: {},
    requestDetailsData: {},
    usagecompanyReportData: {},
    companyPrintingReportData: {},
    empServiceHistoryData: {},
    companySubmissionDetailData: {},

    // Templates
    template: {},
    employee_template: {},

    // Savings & Scores
    savingsOpportunityData: {},
    manageServicesData: {},
    opportunityStatusId: '',

    // Visibility & Company
    serviceCostVisibility: {},
    orgCostVisibility: {},
    company_logo: {},
    companyCurrency: {},
    billingPeriods: [],
    activeBillingPeriod: {},
    ownerServicesData: {},

    // Requests & Status
    requestsData: {},
    CompanyhistoryData: {},
    updateStatusRequest: {},
    UserServiceData: false,

    // Approvals
    approvalRulesData: {},
    newRuleData: {},
    updatedRuleData: {},
    deletdRuleData: {},

    // Downloads
    downloadFileData: {},

    // Missing fields added for referenced data
    requestCommentData: {},
    serviceUsagesData: {},
    serviceData: {},
    compMarkAllNotificationsData: {},
  },

  reducers: {
    startLoading: (state) => {
      state.isLoading = true;
    },
    deleteManagerName: (state) => {
      state.managerName = "";
    },

    // Refactored fetches with setFetchState
    updateEmployees: (state, action) =>
      setFetchState(state, action, {
        endpoint: "/api/v1/company/employees",
        destKey: null, // Custom logic below
        successMessage: "Company employees fetched successfully",
      }) || (() => {
        if (action.payload) {
          state.employeesData = action.payload.employees;
          state.managerName = action.payload.manager_name;
        }
      })(),

    updateSortedEmployees: (state, action) =>
      setFetchState(state, action, {
        endpoint: "/api/v1/company/employees",
        destKey: null,
        successMessage: "Company employees fetched successfully",
      }) || (() => {
        if (action.payload) {
          state.employeesData = action.payload.employees.sort(
            (a, b) => b.monthly_cost - a.monthly_cost
          );
          state.managerName = action.payload.manager_name;
        }
      })(),

    getBillingPeriodsData: (state, action) =>
      setFetchState(state, action, {
        endpoint: "/api/v1/get_billing_periods",
        destKey: "billingPeriods",
      }),

    getMonthReportsResult: (state, action) =>
      setFetchState(state, action, {
        endpoint: "/api/v1/month_reports",
        destKey: "monthReportData",
      }),

    ownerAuthorizedServices: (state, action) =>
      setFetchState(state, action, {
        endpoint: "/api/v1/owner_authorized_services",
        destKey: "ownerServicesData",
      }),

    getUsegesServiceData: (state, action) =>
      setFetchState(state, action, {
        endpoint: "/api/v1/yearly_user_service_details",
        destKey: "YearlyUsegeServiceData",
      }),

    getServiceContractData: (state, action) =>
      setFetchState(state, action, {
        endpoint: "/api/v1/service_contract",
        destKey: "ServiceContractData",
      }),

    getContractdata: (state, action) =>
      setFetchState(state, action, {
        endpoint: "/api/v1/get_contract",
        destKey: "contractData",
      }),

    savingsOpportunities: (state, action) =>
      setFetchState(state, action, {
        endpoint: "/api/v1/savings_opportunities",
        destKey: "savingsOpportunityData",
      }),

    manageServicesDataScore: (state, action) =>
      setFetchState(state, action, {
        endpoint: "/api/v1/manage_service_data_score",
        destKey: "manageServicesData",
      }),

    employeeRequests: (state, action) =>
      setFetchState(state, action, {
        endpoint: `api/v1/company/employees/id/requests`,
        destKey: "employeeRequestData",
        successMessage: "Requests fetched successfully",
      }),

    serviceMember: (state, action) =>
      setFetchState(state, action, {
        endpoint: "api/v1/service_members",
        destKey: "serviceMemberData",
        successMessage: "Service members fetched successfully",
      }),

    ServiceTemplate: (state, action) =>
      setFetchState(state, action, {
        endpoint: `api/v1/template/id`,
        destKey: "template",
        successMessage: "Template fetched successfully",
      }),

    EmployeeTemplate: (state, action) =>
      setFetchState(state, action, {
        endpoint: "api/v1/employee_template",
        destKey: "employee_template",
        successMessage: "Employee template fetched successfully",
      }),

    getIntegrationResult: (state, action) =>
      setFetchState(state, action, {
        endpoint: "api/v1/integrations",
        destKey: "allIntegrationData",
      }),

    getServiceIntegrationResult: (state, action) =>
      setFetchState(state, action, {
        endpoint: `api/v1/integrations/service_integration/service_id`,
        destKey: "serviceIntegrations",
      }),

    updateRequestDetails: (state, action) =>
      setFetchState(state, action, {
        endpoint: `api/v1/personal/requests/{id}`,
        destKey: "requestDetailsData",
        successMessage: "Request details fetched successfully",
      }),

    updateRequests: (state, action) =>
      setFetchState(state, action, {
        endpoint: "api/v1/personal/requests",
        destKey: "requestsData",
        successMessage: "Requests fetched successfully",
      }),

    updateCompanyExpenses: (state, action) =>
      setFetchState(state, action, {
        endpoint: "api/v1/company_expenses",
        destKey: "companyExpenses",
        successMessage: "User expenses fetched successfully!",
      }),

    employeeSubmissions: (state, action) =>
      setFetchState(state, action, {
        endpoint: `api/v1/company/employees/id/submissions`,
        destKey: "employeeSubmissionData",
        successMessage: "Submissions fetched successfully",
      }),

    costVisibilityData: (state, action) =>
      setFetchState(state, action, {
        endpoint: "api/v1/get_cost_visibility",
        destKey: "serviceCostVisibility",
      }),

    costOrgVisibilityData: (state, action) =>
      setFetchState(state, action, {
        endpoint: "api/v1/get_org_cost_visibility",
        destKey: "orgCostVisibility",
      }),

    empSubmission: (state, action) =>
      setFetchState(state, action, {
        endpoint: `api/v1/company/employees/id/submissions?submission_id`,
        destKey: "empSubmissionDetail",
        successMessage: "Submission details fetched successfully",
      }),

    employeeFeedbacks: (state, action) =>
      setFetchState(state, action, {
        endpoint: "api/v1/company/employees/:id/feedbacks",
        destKey: "employeefeedbackData",
        successMessage: "Feedbacks fetched successfully",
      }),

    employeeFeedbackslist: (state, action) =>
      setFetchState(state, action, {
        endpoint: `api/v1/company/employees/emp_id/feedbacks?feedback_id=feed_id`,
        destKey: "employeefeedbacksingle",
        successMessage: "Feedback details fetched successfully",
      }),

    fetchApprovalRules: (state, action) =>
      setFetchState(state, action, {
        endpoint: "/api/v1/approval_rules",
        destKey: "approvalRulesData",
      }),

    servicesDetailData: (state, action) =>
      setFetchState(state, action, {
        endpoint: "api/v1/company/services/company_services",
        destKey: "servicesData",
        successMessage: 'fetched successfully'
      }),

    servicesSortedDetailData: (state, action) => {
      setFetchState(state, action, {
        endpoint: "api/v1/company/services/company_services",
        destKey: "servicesData",
        successMessage: 'fetched successfully'
      });
      // Uncomment and adjust sorting if needed
      // if (state.success && state.servicesData?.company_services) {
      //   state.servicesData.company_services.sort((a, b) => b.cost_per_month - a.cost_per_month);
      // }
    },

    SetServicesData: (state, action) =>
      setFetchState(state, action, {
        endpoint: "api/v1/setup_services",
        destKey: "setupServiceData",
        successMessage: 'fetched successfully'
      }),

    authorizedServices: (state, action) =>
      setFetchState(state, action, {
        endpoint: "/api/v1/authorized_services",
        destKey: "authorizedServicesData",
        successMessage: 'fetched successfully'
      }),

    handleDownloadFile: (state, action) =>
      setFetchState(state, action, {
        endpoint: "/api/v1/download_file/id",
        destKey: "downloadFileData",
        successMessage: 'fetched successfully'
      }),

    InitializeServicesData: (state, action) =>
      setFetchState(state, action, {
        endpoint: "/api/v1/initialize_services_data",
        destKey: "CategoriesAndUsersData",
        successMessage: 'fetched successfully'
      }),

    allEmpData: (state, action) =>
      setFetchState(state, action, {
        endpoint: "/api/v1/all_employees",
        destKey: "allEmployees",
        successMessage: 'fetched successfully'
      }),

    // Added missing fetch reducers
    homeData: (state, action) =>
      setFetchState(state, action, {
        endpoint: "/api/v1/company_dashboard",
        destKey: "homeData",
      }),

    employeeData: (state, action) =>
      setFetchState(state, action, {
        endpoint: "/api/v1/company/employees",
        destKey: "employeeData",
      }),

    submissionsData: (state, action) =>
      setFetchState(state, action, {
        endpoint: "api/v1/company/services/submissions",
        destKey: "submissionsData",
      }),

    submissionsRecord: (state, action) =>
      setFetchState(state, action, {
        endpoint: "api/v1/company/services/single_service_submissions",
        destKey: "submissionsRecord",
      }),

    CompanyhistoryData: (state, action) =>
      setFetchState(state, action, {
        endpoint: "api/v1/company/costs_history",
        destKey: "CompanyhistoryData",
      }),

    usageReportData: (state, action) =>
      setFetchState(state, action, {
        endpoint: "api/v1/company/services/usage_report",
        destKey: "usageReportData",
      }),

    printingUsageReportData: (state, action) =>
      setFetchState(state, action, {
        endpoint: "api/v1/company/services/usage_report_printing",
        destKey: "printingUsageReportData",
      }),

    SubmissionsListData: (state, action) =>
      setFetchState(state, action, {
        endpoint: "/api/v1/company/services/service_submissions",
        destKey: "SubmissionsListData",
      }),

    specficFeedbackData: (state, action) =>
      setFetchState(state, action, {
        endpoint: "api/v1/company/services/usage_report/feedbacks",
        destKey: "specficFeedbackData",
      }),

    usageReportRequestData: (state, action) =>
      setFetchState(state, action, {
        endpoint: "api/v1/company/services/usage_report/requests",
        destKey: "usageReportRequestData",
      }),

    usageReportServiceData: (state, action) =>
      setFetchState(state, action, {
        endpoint: "api/v1/company/services/usage_report/service_users",
        destKey: "usageReportServiceData",
      }),

    usagecompanyReportData: (state, action) =>
      setFetchState(state, action, {
        endpoint: "api/v1/company/services/usage_report/service_users",
        destKey: "usagecompanyReportData",
      }),

    companyPrintingReportData: (state, action) =>
      setFetchState(state, action, {
        endpoint: "api/v1/services/employees/usage_report_printing",
        destKey: "companyPrintingReportData",
      }),

    empServiceHistoryData: (state, action) =>
      setFetchState(state, action, {
        endpoint: "api/v1/services/employees/costs_history",
        destKey: "empServiceHistoryData",
      }),

    companySubmissionDetailData: (state, action) =>
      setFetchState(state, action, {
        endpoint: "api/v1/company/submissions",
        destKey: "companySubmissionDetailData",
      }),

    usageReportFeedbacksData: (state, action) =>
      setFetchState(state, action, {
        endpoint: "api/v1/company/services/usage_report/feedbacks",
        destKey: "usageReportFeedbacksData",
      }),

    // Refactored mutations with handleApiResponse
    notifyMultiServiceOwner: (state, action) =>
      handleApiResponse(state, action, {
        endpoint: "/api/v1/notify_multiple_service_owners",
        successMessage: "Email sent successfully",
        successStatuses: [200],
        onSuccess: () => showSuccessToast("Email sent successfully"),
      }),

    notifyServiceOwner: (state, action) =>
      handleApiResponse(state, action, {
        endpoint: "/api/v1/notify_service_owners",
        successMessage: "Email sent successfully",
        successStatuses: [200],
        onSuccess: () => showSuccessToast("Email sent successfully"),
      }),

    notifyPublishReport: (state, action) =>
      handleApiResponse(state, action, {
        endpoint: "/api/v1/company_publish_report",
        successMessage: "Report published successfully",
        dataKey: "publishCompanyReport",
        successStatuses: [200],
        onSuccess: () => showSuccessToast("Report published successfully"),
      }),

    customerPublishedReport: (state, action) =>
      handleApiResponse(state, action, {
        endpoint: "/api/v1/customer_publish_report",
        successMessage: "Report published successfully",
        dataKey: "customerPublishData",
        successStatuses: [200],
        onSuccess: () => showSuccessToast("Report published successfully"),
      }),

    notifyPublishMonthlyReport: (state, action) =>
      handleApiResponse(state, action, {
        endpoint: "/api/v1/company_publish_monthly_report",
        dataKey: "publishCompanyReportTime",
        successStatuses: [200],
      }),

    updateCurrency: (state, action) =>
      handleApiResponse(state, action, {
        endpoint: "/api/v1/update_company_currency",
        successMessage: "Currency updated successfully",
        onSuccess: (s, payloadData) => {
          s.companyCurrency = payloadData?.company?.operating_currency;
          showSuccessToast("Currency updated successfully");
        },
        successStatuses: [200],
      }),

    updateActiveBillingPeriod: (state, action) =>
      handleApiResponse(state, action, {
        endpoint: "/api/v1/update_active_billing_period",
        successMessage: "Active billing period updated successfully",
        onSuccess: (s, payloadData) => {
          s.activeBillingPeriod = payloadData?.active_billing_period;
          showSuccessToast("Active billing period updated successfully");
        },
        successStatuses: [200],
      }),

    // Refactored switch-based reducers to handleApiResponse
    createRequestComment: (state, action) =>
      handleApiResponse(state, action, {
        endpoint: `api/v1/personal/requests/id/comment`,
        successMessage: "Request comment created successfully",
        dataKey: "requestCommentData",
        successStatuses: [201],
        customErrors: {
          400: "Bad Request: Invalid data provided",
          401: "Unauthorized: Please authenticate",
          403: "Forbidden: You do not have permission to perform this action",
          500: "Internal Server Error: Something went wrong",
        },
        failMessage: "Request comment creation failed",
      }),

    updaterequestData: (state, action) =>
      handleApiResponse(state, action, {
        endpoint: `api/v1/personal/requests/id`,
        successMessage: "Request updated successfully",
        dataKey: "requestDetailsData",
        successStatuses: [201],
        customErrors: {
          400: "Bad Request: Invalid data provided",
          401: "Unauthorized: Please authenticate",
          403: "Forbidden: You do not have permission to perform this action",
          500: "Internal Server Error: Something went wrong",
        },
        failMessage: "Update Request failed",
      }),

    createService: (state, action) =>
      handleApiResponse(state, action, {
        endpoint: "api/v1/add_services",
        successMessage: "Service created successfully",
        dataKey: "newServicesData",
        successStatuses: [201],
        onSuccess: (s) => {
          s.serviceCreated = true;
        },
      }),

    createdIntegration: (state, action) =>
      handleApiResponse(state, action, {
        endpoint: "/api/v1/integrations",
        successMessage: "Integration created successfully",
        dataKey: "newIntegrationData",
        successStatuses: [201],
        onSuccess: (s) => {
          s.integrationCreated = true;
        },
      }),

    updatedIntegration: (state, action) =>
      handleApiResponse(state, action, {
        endpoint: `/api/v1/integrations/id`,
        successMessage: "Integration updated successfully",
        dataKey: "newIntegrationData",
        successStatuses: [200],
        onSuccess: (s) => {
          s.integrationUpdated = true;
        },
      }),

    contractData: (state, action) =>
      handleApiResponse(state, action, {
        endpoint: "/api/v1/upload_contract_file",
        successMessage: "Contract created successfully",
        successStatuses: [201],
        onSuccess: (s) => {
          s.createContract = true;
        },
      }),

    serviceUseges: (state, action) =>
      handleApiResponse(state, action, {
        endpoint: "api/v1/create_useges_services",
        successMessage: "Service usage created successfully",
        dataKey: "serviceUsagesData",
        successStatuses: [201],
        onSuccess: (s, payloadData) => {
          s.serviceUsagesData = {
            ...(s.serviceUsagesData || {}),
            ...(payloadData || {}),
          };
        },
      }),

    employeeFileData: (state, action) =>
      handleApiResponse(state, action, {
        endpoint: "api/v1/employee_files",
        successMessage: "Employee data uploaded",
        successStatuses: [201],
        onSuccess: (s) => {
          s.userMenuUpdated = true;
        },
      }),

    createdProcurement: (state, action) =>
      handleApiResponse(state, action, {
        endpoint: "api/v1/create_procurement",
        successMessage: "Procurement created successfully",
        dataKey: "serviceData",
        successStatuses: [201],
      }),

    addServiceMember: (state, action) =>
      handleApiResponse(state, action, {
        endpoint: "api/v1/add_service_member",
        successMessage: "Service Member created successfully",
        successStatuses: [201],
        onSuccess: (s) => {
          s.serviceMemberCreated = true;
        },
      }),

    deletedIntegrationData: (state, action) =>
      handleApiResponse(state, action, {
        endpoint: "api/v1/integrations",
        successMessage: "Integration deleted successfully",
        dataKey: "deletedIntegration",
        successStatuses: [200],
      }),

    updateStatus: (state, action) =>
      handleApiResponse(state, action, {
        endpoint: "api/v1/personal/requests/{id}",
        successMessage: 'Status updated successfully',
        dataKey: "updateStatusRequest",
        successStatuses: [200],
      }),

    updatedProcurement: (state, action) =>
      handleApiResponse(state, action, {
        endpoint: "api/v1/update_procurement/{id}",
        successMessage: 'Procurement updated successfully',
        successStatuses: [200],
      }),

    updatedUsegesService: (state, action) =>
      handleApiResponse(state, action, {
        endpoint: `api/v1/update_usage_service/id`,
        successMessage: 'Usage Service updated successfully',
        successStatuses: [200],
        onSuccess: (s) => {
          s.UserServiceData = true;
        },
      }),

    updatedContractData: (state, action) =>
      handleApiResponse(state, action, {
        endpoint: "api/v1/update_contract/id",
        successMessage: 'Contract Data updated successfully',
        successStatuses: [200],
      }),

    editContractDetails: (state, action) =>
      handleApiResponse(state, action, {
        endpoint: "api/v1/edit_contract/id",
        successMessage: 'Contract Data updated successfully',
        successStatuses: [200],
        onSuccess: (s) => {
          s.createContract = true;
        },
      }),

    updatedContractFileData: (state, action) =>
      handleApiResponse(state, action, {
        endpoint: "api/v1/update_contract_file",
        successMessage: 'Contract file uploaded successfully',
        successStatuses: [200],
        onSuccess: (s) => {
          s.createContract = true;
        },
      }),

    updateOpportunityStatus: (state, action) =>
      handleApiResponse(state, action, {
        endpoint: `/api/v1/savings_opportunities/id/update_opportunity_status`,
        successMessage: 'Status updated successfully',
        onSuccess: (s, payloadData) => {
          s.opportunityStatusId = payloadData?.savings_opportunity;
        },
        successStatuses: [200],
      }),

    deactivatedService: (state, action) =>
      handleApiResponse(state, action, {
        endpoint: `pi/v1/deactivate_service/id`,
        successMessage: 'Service deactivated successfully',
        successStatuses: [200],
        onSuccess: (s) => {
          s.serviceMenuUpdated = true;
        },
      }),

    activatedService: (state, action) =>
      handleApiResponse(state, action, {
        endpoint: `api/v1/activate_service/id`,
        successMessage: 'Service activated successfully',
        successStatuses: [200],
        onSuccess: (s) => {
          s.serviceMenuUpdated = true;
        },
      }),

    deletedService: (state, action) =>
      handleApiResponse(state, action, {
        endpoint: `api/v1/destroy_service/id`,
        successMessage: 'Service deleted successfully',
        successStatuses: [200],
        onSuccess: (s) => {
          s.serviceMenuUpdated = true;
        },
      }),

    updatedServiceName: (state, action) =>
      handleApiResponse(state, action, {
        endpoint: `api/v1/update_service_name/id`,
        successMessage: 'Service Name updated successfully',
        successStatuses: [200],
        onSuccess: (s) => {
          s.serviceUpdated = true;
        },
      }),

    updatedServicePocName: (state, action) =>
      handleApiResponse(state, action, {
        endpoint: `api/v1/update_service_poc_name/id`,
        successMessage: 'Procurement contact updated successfully',
        successStatuses: [200],
        onSuccess: (s) => {
          s.serviceUpdated = true;
        },
      }),

    updatedServiceCategory: (state, action) =>
      handleApiResponse(state, action, {
        endpoint: `api/v1/update_service_category/id`,
        successMessage: 'Service Category updated successfully',
        successStatuses: [200],
        onSuccess: (s) => {
          s.serviceUpdated = true;
        },
      }),

    updateMemberPermission: (state, action) =>
      handleApiResponse(state, action, {
        endpoint: `api/v1/update_service_member_permission`,
        successMessage: 'Permission updated successfully',
        successStatuses: [200],
        onSuccess: (s) => {
          s.serviceMemberUpdated = true;
        },
      }),

    deletedServiceMember: (state, action) =>
      handleApiResponse(state, action, {
        endpoint: "api/v1/destroy_service_member",
        successMessage: 'Service Member deleted successfully',
        successStatuses: [200],
        onSuccess: (s) => {
          s.serviceMemberUpdated = true;
        },
      }),

    deletedContract: (state, action) =>
      handleApiResponse(state, action, {
        endpoint: `/api/v1/delete_contract_file/contractId`,
        successMessage: 'Contract file deleted successfully',
        successStatuses: [200],
        onSuccess: (s) => {
          s.createContract = true;
        },
      }),

    deletedUserData: (state, action) =>
      handleApiResponse(state, action, {
        endpoint: `/api/v1/personal/users/userId/delete_user`,
        successMessage: 'User deleted successfully',
        successStatuses: [200],
        onSuccess: (s) => {
          s.userMenuUpdated = true;
        },
      }),

    updateUserRole: (state, action) =>
      handleApiResponse(state, action, {
        endpoint: `/api/v1/personal/users/user_id/update_user_role`,
        successMessage: 'Role updated successfully',
        successStatuses: [200],
        onSuccess: (s) => {
          s.userMenuUpdated = true;
        },
      }),

    deactivatedEmployee: (state, action) =>
      handleApiResponse(state, action, {
        endpoint: `/api/v1/personal/users/user_id/deactivate_employee`,
        successMessage: 'Employee deactivated successfully',
        successStatuses: [200],
        onSuccess: (s) => {
          s.userMenuUpdated = true;
        },
      }),

    activatedEmployee: (state, action) =>
      handleApiResponse(state, action, {
        endpoint: `/api/v1/personal/users/user_id/activate_employee`,
        successMessage: 'Employee activated successfully',
        successStatuses: [200],
        onSuccess: (s) => {
          s.userMenuUpdated = true;
        },
      }),

    resetPassword: (state, action) =>
      handleApiResponse(state, action, {
        endpoint: `/api/v1/company/employees/user_id/reset_password_email`,
        successMessage: "Reset password email sent successfully",
        successStatuses: [200],
      }),

    addApprovalRule: (state, action) =>
      handleApiResponse(state, action, {
        endpoint: "/api/v1/approval_rules",
        successMessage: "Approval Flow created successfully",
        dataKey: "newRuleData",
        successStatuses: [201],
      }),

    updateApprovalRule: (state, action) =>
      handleApiResponse(state, action, {
        endpoint: "/api/v1/approval_rules",
        successMessage: "Approval Flow updated successfully",
        dataKey: "updatedRuleData",
        successStatuses: [200],
      }),

    deleteApprovalRule: (state, action) =>
      handleApiResponse(state, action, {
        endpoint: "/api/v1/approval_rules",
        successMessage: "Approval Flow deleted successfully",
        dataKey: "deletdRuleData",
        successStatuses: [200],
      }),

    destroyApprovalStep: (state, action) =>
      handleApiResponse(state, action, {
        endpoint: "/api/v1/approval_steps",
        successMessage: "Approval Step deleted successfully",
        successStatuses: [200],
      }),

    updateNotifications: (state, action) =>
      setFetchState(state, action, {
        endpoint: "api/v1/company/notifications",
        destKey: "companyNotificationsData",
        successMessage: "Feedbacks fetched successfully",
      }),

    markAllReadNotification: (state, action) =>
      handleApiResponse(state, action, {
        endpoint: "api/v1/personal/notifications/mark_all_read",
        successMessage: "Notifications mark all read successfully",
        dataKey: "compMarkAllNotificationsData",
        defaultFailMessage: "Notifications mark all read failed",
        successStatuses: [200, 201],
      }),

    // Simple state updates (no API)
    setUserServiceFalse: (state) => {
      state.UserServiceData = false;
    },
    setServiceContractFasle: (state) => {
      state.createContract = false;
    },
    setUserMenuUpdatedFalse: (state) => {
      state.userMenuUpdated = false;
    },
    setServiceMenuUpdatedFalse: (state) => {
      state.serviceMenuUpdated = false;
    },
    setServiceMemberUpdatedFalse: (state) => {
      state.serviceMemberUpdated = false;
    },
    setServiceMemberCreatedFalse: (state) => {
      state.serviceMemberCreated = false;
    },
    setServiceUpdatedFalse: (state) => {
      state.serviceUpdated = false;
    },
    setServiceCreatedFalse: (state) => {
      state.serviceCreated = false;
    },
    setIntegrationCreatedFalse: (state) => {
      state.integrationCreated = false;
      state.integrationUpdated = false;
    },
    updatedpersonalServices: (state, action) => {
      state.servicesData.company_services = action.payload;
    },
    updatedSubmission: (state, action) => {
      state.SubmissionsListData.submitted_services = action.payload;
    },
    updateEmployeeSubmission: (state, action) => {
      state.employeeSubmissionData.submissions = action.payload;
    },
    updateUserRequest: (state, action) => {
      if (state.usageReportServiceData) {
        state.usageReportServiceData.service_users = action.payload;
      }
    },
    updatedEmployees: (state, action) => {
      if (state.employeesData) {
        state.employeesData = action.payload;
      }
    },
    updatedCompanyRequests: (state, action) => {
      if (action.payload) {
        state.employeeRequestData = {
          ...state.employeeRequestData,
          requests: action.payload
        };
      }
    },
    updatedEmployeeRequests: (state, action) => {
      if (action.payload) {
        state.usageReportRequestData = {
          ...state.usageReportRequestData,
          requests: action.payload
        };
      }
    },
    teamSubmissions: (state, action) => {
      if (action.payload) {
        state.employeeSubmissionData.submissions = action.payload.submissions
        state.employeeSubmissionData.total_count = action.payload.total_count
        state.employeeSubmissionData.user_name = action.payload.user_name
      }
    },
    teamFeedbacks: (state, action) => {
      if (action.payload) {
        state.employeefeedbackData = action.payload
      }
    },
    teamRequests: (state, action) => {
      if (action.payload) {
        state.employeeRequestData.requests = action.payload.requests
        state.employeeRequestData.total_count = action.payload.total_count
        state.employeeRequestData.user_name =action.payload.user_name
      }
    },
    setEmptyUsageReport: (state) => {
      state.usageReportData = {}
    },
    setEmptyEmployeeData: (state) => {
      state.employeeData = undefined;
    },
    setEmptyUsageCompanyReportData: (state) => {
      state.usagecompanyReportData = undefined;
    },

    // Custom non-API reducers (kept as-is, minor fixes)
    companyIcon: (state, action) => {
      state.isLoading = false;
      if (action.payload) {
        state.success = true;
        state.endPoint = "api/v1/upload_company_icon";
        state.company_logo = action.payload.data.company_image.record.updated_at
      } else {
        state.success = false;
        state.endPoint = "api/v1/upload_company_icon";
        state.message = action?.payload?.data?.error;
        showErrorToast(state.message);
      }
    },

    costVisibility: (state, action) => {
      state.isLoading = false;
      if (action.payload) {
        state.success = true;
        state.endPoint = "api/v1/cost_visibility";
      } else {
        state.success = false;
        state.endPoint = "api/v1/cost_visibility";
        state.message = action?.payload?.data?.error;
        showErrorToast(state.message);
      }
    },

    costOrgVisibility: (state, action) => {
      state.isLoading = false;
      if (action.payload) {
        state.success = true;
        state.endPoint = "api/v1/org_cost_visibility";
      } else {
        state.success = false;
        state.endPoint = "api/v1/org_cost_visibility";
        state.message = action?.payload?.data?.error;
        showErrorToast(state.message);
      }
    },
  },
});

export const {
  startLoading,
  deleteManagerName,
  employeeRequests,
  employeeSubmissions,
  companyIcon,
  costVisibility,
  costVisibilityData,
  costOrgVisibility,
  costOrgVisibilityData,
  empSubmission,
  updateEmployees,
  updateSortedEmployees,
  employeeFeedbacks,
  teamSubmissions,
  teamFeedbacks,
  teamRequests,
  employeeFeedbackslist,
  updateStatus,
  createRequestComment,
  updaterequestData,
  updateRequestDetails,
  updateRequests,
  updatedCompanyRequests,
  updatedEmployeeRequests,
  servicesDetailData,
  servicesSortedDetailData,
  updatedpersonalServices,
  updateUserRequest,
  updatedEmployees,
  updatedSubmission,
  updateEmployeeSubmission,
  authorizedServices,
  InitializeServicesData,
  allEmpData,
  deactivatedEmployee,
  activatedEmployee,
  deactivatedService,
  activatedService,
  deletedService,
  serviceUseges,
  contractData,
  employeeFileData,
  createdProcurement,
  updatedProcurement,
  updatedUsegesService,
  updatedContractData,
  updatedContractFileData,
  setServiceCreatedFalse,
  setServiceUpdatedFalse,
  setServiceMemberCreatedFalse,
  setServiceMemberUpdatedFalse,
  setServiceMenuUpdatedFalse,
  setUserMenuUpdatedFalse,
  setUserServiceFalse,
  setServiceContractFasle,
  handleDownloadFile,
  deletedServiceMember,
  deletedContract,
  deletedUserData,
  updateUserRole,
  addServiceMember,
  serviceMember,
  ServiceTemplate,
  EmployeeTemplate,
  updateMemberPermission,
  updatedServiceName,
  updatedServicePocName,
  updatedServiceCategory,
  savingsOpportunities,
  getUsegesServiceData,
  getServiceContractData,
  getContractdata,
  manageServicesDataScore,
  notifyMultiServiceOwner,
  notifyServiceOwner,
  notifyPublishReport,
  setEmptyUsageReport,
  setEmptyEmployeeData,
  setEmptyUsageCompanyReportData,
  notifyPublishMonthlyReport,
  updateOpportunityStatus,
  editContractDetails,
  updateCompanyExpenses,
  updateCurrency,
  updateActiveBillingPeriod,
  getBillingPeriodsData,
  ownerAuthorizedServices,
  createdIntegration,
  updatedIntegration,
  getIntegrationResult,
  getServiceIntegrationResult,
  setIntegrationCreatedFalse,
  customerPublishedReport,
  getMonthReportsResult,
  resetPassword,
  addApprovalRule,
  fetchApprovalRules,
  updateApprovalRule,
  deletedIntegrationData,
  deleteApprovalRule,
  destroyApprovalStep,
  // Added missing exports for fetch actions
  homeData,
  employeeData,
  submissionsData,
  submissionsRecord,
  CompanyhistoryData,
  usageReportData,
  printingUsageReportData,
  SubmissionsListData,
  specficFeedbackData,
  usageReportRequestData,
  usageReportServiceData,
  usagecompanyReportData,
  companyPrintingReportData,
  empServiceHistoryData,
  companySubmissionDetailData,
  usageReportFeedbacksData,
  SetServicesData,
  createService,
  updateNotifications,
  markAllReadNotification,
} = companyDashboardSlice.actions;

export const selectCompanyDashboard = (state) => state.companyDashboard;

// Refactored fetchData to use specific actions instead of generic updateData
const fetchData = (url, actionCreator) => (dispatch) => {
  dispatch(startLoading());
  fetchWithToken(url, dispatch, actionCreator);
};

// Updated thunks to use refactored actions (minor fixes for consistency)
export const getYearlyUsegesServiceData = (service_id, year) => (dispatch) => {
  fetchWithToken(`/api/v1/yearly_user_service_details/${service_id}?year=${year}`, dispatch, getUsegesServiceData);
};

export const getServiceContract = (service_id) => (dispatch) => {
  fetchWithToken(`/api/v1/service_contract/${service_id}`, dispatch, getServiceContractData);
};

export const getContractData = (id) => (dispatch) => {
  fetchWithToken(`/api/v1/get_contract/${id}`, dispatch, getContractdata);
};

export const downloadFile = (id) => (dispatch) => {
  fetchWithToken(`/api/v1/download_file/${id}`, dispatch, handleDownloadFile);
};

export const getAuthorizedServices = () => (dispatch) => {
  fetchWithToken(`/api/v1/authorized_services`, dispatch, authorizedServices);
};

export const getInitializeServicesData = () => (dispatch) => {
  fetchWithToken(`/api/v1/initialize_services_data`, dispatch, InitializeServicesData);
};

export const getAll_employees = () => (dispatch) => {
  fetchWithToken(`/api/v1/all_employees`, dispatch, allEmpData);
};

export const createServices = (serviceData) => (dispatch) => {
  postWithToken(`api/v1/add_services`, serviceData, dispatch, createService);
};

export const createIntegration = (integrationData) => (dispatch) => {
  postWithToken(`/api/v1/integrations`, integrationData, dispatch, createdIntegration);
};

export const updateIntegration = (id, integrationData) => (dispatch) => {
  patchWithToken(`/api/v1/integrations/${id}`, integrationData, dispatch, updatedIntegration);
};

export const getIntegrations = () => (dispatch) => {
  fetchWithToken(`/api/v1/integrations`, dispatch, getIntegrationResult);
};

export const deleteIntegration = (id) => (dispatch) => {
  deleteWithToken(`/api/v1/integrations/${id}`, dispatch, deletedIntegrationData);
};

export const getServiceIntegrations = (id) => (dispatch) => {
  fetchWithToken(`/api/v1/integrations/service_integration/${id}`, dispatch, getServiceIntegrationResult);
};

export const createProcurement = (serviceData) => (dispatch) => {
  postWithToken(`api/v1/create_procurement`, serviceData, dispatch, createdProcurement);
};

export const updateProcurement = (id, serviceData) => (dispatch) => {
  patchWithToken(`api/v1/update_procurement/${id}`, serviceData, dispatch, updatedProcurement);
};

export const updateServiceOwner = (id, servicesData) => (dispatch) => {
  patchWithToken(`api/v1/update_service_owner/${id}`, servicesData, dispatch, updateStatus);
};

export const updateServices = (id, servicesData) => (dispatch) => {
  patchWithToken(`api/v1/update_service/${id}`, servicesData, dispatch, updateStatus);
};

export const createCustomerServiceUseges = (usegesData) => (dispatch) => {
  const token = localStorage.getItem("accessToken");
  postRequest(`api/v1/create_useges_services`, usegesData, {
    "Content-Type": "multipart/form-data",
    Authorization: `Bearer ${token}`,
  }).then((res) => {
    dispatch(serviceUseges(res));
  });
};

export const updateCustomerServiceUseges = (id, usegesData) => (dispatch) => {
  const token = localStorage.getItem("accessToken");
  patchRequest(`api/v1/update_usage_service/${id}`, usegesData, {
    "Content-Type": "multipart/form-data",
    Authorization: `Bearer ${token}`,
  }).then((res) => {
    dispatch(updatedUsegesService(res));
  });
};

export const updateCustomerServiceUsegesReport = (id, usegesData) => (dispatch) => {
  const token = localStorage.getItem("accessToken");
  patchRequest(`api/v1/update_usage_service_report/${id}`, usegesData, {
    "Content-Type": "multipart/form-data",
    Authorization: `Bearer ${token}`,
  }).then((res) => {
    dispatch(updatedUsegesService(res));
  });
};

export const createContractData = (Data) => (dispatch) => {
  const token = localStorage.getItem("accessToken");
  postRequest(`api/v1/upload_contract_file`, Data, {
    "Content-Type": "multipart/form-data",
    Authorization: `Bearer ${token}`,
  }).then((res) => {
    dispatch(contractData(res));
  });
};

export const editContractData = (id, Data) => (dispatch) => {
  const token = localStorage.getItem("accessToken");
  patchRequest(`/api/v1/edit_contract/${id}`, Data, {
    "Content-Type": "multipart/form-data",
    Authorization: `Bearer ${token}`,
  }).then((res) => {
    dispatch(editContractDetails(res));
  });
};

export const updateContractData = (id, usegesData) => (dispatch) => {
  const token = localStorage.getItem("accessToken");
  patchRequest(`api/v1/update_contract/${id}`, usegesData, {
    "Content-Type": "multipart/form-data",
    Authorization: `Bearer ${token}`,
  }).then((res) => {
    dispatch(updatedContractData(res));
  });
};

export const updateContractFile = (usegesData) => (dispatch) => {
  const token = localStorage.getItem("accessToken");
  patchRequest(`api/v1/update_contract_file`, usegesData, {
    "Content-Type": "multipart/form-data",
    Authorization: `Bearer ${token}`,
  }).then((res) => {
    dispatch(updatedContractFileData(res));
  });
};

export const createEmployeeFile = (Data) => (dispatch) => {
  const token = localStorage.getItem("accessToken");
  postRequest(`api/v1/employee_files`, Data, {
    "Content-Type": "multipart/form-data",
    Authorization: `Bearer ${token}`,
  }).then((res) => {
    dispatch(employeeFileData(res));
  });
};

export const createServiceMember = (memberData) => (dispatch) => {
  postWithToken(`api/v1/add_service_member`, memberData, dispatch, addServiceMember);
};

export const updateServiceMemberPermission = (memberData) => (dispatch) => {
  patchWithToken(`api/v1/update_service_member_permission`, memberData, dispatch, updateMemberPermission);
};

export const updateEmployeeRole = (user_id, user_role) => (dispatch) => {
  patchWithToken(`/api/v1/personal/users/${user_id}/update_employee_role`, user_role, dispatch, updateUserRole);
};

export const disableEmployee = (userId) => (dispatch) => {
  deleteWithToken(`/api/v1/personal/users/${userId}/disable_employee`, dispatch, deletedUserData);
};

export const deactivateEmpReport = (user_id) => (dispatch) => {
  patchWithToken(`/api/v1/personal/users/${user_id}/deactivate_employee`, {}, dispatch, deactivatedEmployee);
};

export const activateEmpReport = (user_id) => (dispatch) => {
  patchWithToken(`/api/v1/personal/users/${user_id}/activate_employee`, {}, dispatch, activatedEmployee);
};

export const updateServicename = (id, servicesData) => (dispatch) => {
  patchWithToken(`/api/v1/update_service_name/${id}`, servicesData, dispatch, updatedServiceName);
};

export const updateServicePocName = (id, servicesData) => (dispatch) => {
  patchWithToken(`/api/v1/update_service_poc_name/${id}`, servicesData, dispatch, updatedServicePocName);
};

export const updateServicecategory = (id, servicesData) => (dispatch) => {
  patchWithToken(`/api/v1/update_service_category/${id}`, servicesData, dispatch, updatedServiceCategory);
};

export const getServiceMembers = (id) => (dispatch) => {
  fetchWithToken(`/api/v1/service_members/${id}`, dispatch, serviceMember);
};

export const getServiceTemplate = (id) => (dispatch) => {
  fetchWithToken(`/api/v1/template/${id}`, dispatch, ServiceTemplate);
};

export const getEmployeeTemplate = () => (dispatch) => {
  fetchWithToken(`/api/v1/employee_template`, dispatch, EmployeeTemplate);
};

export const deleteServiceMembers = (serviceId, userId) => (dispatch) => {
  deleteWithToken(`/api/v1/destroy_service_member/${serviceId}?user_id=${userId}`, dispatch, deletedServiceMember);
};

export const deleteContract = (contractId, fileID) => (dispatch) => {
  deleteWithToken(`/api/v1/delete_contract_file/${contractId}?file_id=${fileID}`, dispatch, deletedContract);
};

export const deactivateService = (id) => (dispatch) => {
  patchWithToken(`/api/v1/deactivate_service/${id}`, {}, dispatch, deactivatedService);
};

export const activateService = (id) => (dispatch) => {
  patchWithToken(`/api/v1/activate_service/${id}`, {}, dispatch, activatedService);
};

export const deleteService = (id) => (dispatch) => {
  deleteWithToken(`/api/v1/destroy_service/${id}`, dispatch, deletedService);
};

export const teamEmpRequestsData = (id) => (dispatch) => {
  fetchWithToken(`/api/v1/personal/users/${id}/employee_requests`, dispatch, teamRequests);
};

export const employeeRequestsData = (id) => (dispatch) => {
  fetchWithToken(`/api/v1/company/employees/${id}/requests`, dispatch, employeeRequests);
};

export const createRequestComments = (id, requestData) => (dispatch) => {
  postWithToken(`api/v1/personal/requests/${id}/comment`, requestData, dispatch, createRequestComment);
};

export const updateRequest = (id, requestData) => (dispatch) => {
  postWithToken(`api/v1/personal/requests/${id}`, requestData, dispatch, updaterequestData);
};

export const getRequestDetailsdata = (empid, reqid) => (dispatch) => {
  fetchWithToken(`api/v1/company/employees/${empid}/requests?request_id=${reqid}`, dispatch, updateRequestDetails);
};

export const statusUpdateRequest = (id, requestData) => (dispatch) => {
  patchWithToken(`api/v1/personal/requests/${id}`, requestData, dispatch, updateStatus);
};

export const UploadCompanyIcon = (image) => (dispatch) => {
  const token = localStorage.getItem("accessToken");
  const formData = new FormData();
  formData.append("image", image);

  patchRequest(`api/v1/upload_company_icon`, formData, {
    "Content-Type": "multipart/form-data",
    Authorization: `Bearer ${token}`,
  }).then((res) => {
    dispatch(companyIcon(res));
  });
};

export const UpdateCostVisibility = (id, cost) => (dispatch) => {
  const visible = {
    "service_id": id,
    "cost_visibility": cost
  };

  patchWithToken(`api/v1/cost_visibility`, visible, dispatch, costVisibility);
};

export const getCostVisibility = (id) => (dispatch) => {
  fetchWithToken(`api/v1/get_cost_visibility?service_id=${id}`, dispatch, costVisibilityData);
};

export const UpdateOrgCostVisibility = (cost) => (dispatch) => {
  const visible = {
    "cost_visibility": cost
  };

  patchWithToken(`api/v1/org_cost_visibility`, visible, dispatch, costOrgVisibility);
};

export const getOrgCostVisibility = () => (dispatch) => {  // Removed unused id param
  fetchWithToken("api/v1/get_org_cost_visibility", dispatch, costOrgVisibilityData);
};

export const getComRequestsPagewithoutParams = () => (dispatch) => {
  fetchWithToken("api/v1/personal/requests", dispatch, updateRequests);
};

export const employeeSubmissionsData = (id) => (dispatch) => {
  fetchWithToken(`/api/v1/company/employees/${id}/submissions`, dispatch, employeeSubmissions);
};

export const empSubmissionData = (id, submission_id) => (dispatch) => {
  fetchWithToken(`api/v1/company/employees/${id}/submissions?submission_id=${submission_id}`, dispatch, empSubmission);
};

export const teamEmpSubmissionsData = (id) => (dispatch) => {
  fetchWithToken(`/api/v1/personal/users/${id}/employee_submissions`, dispatch, teamSubmissions);
};

export const teamEmpFeedbacksData = (id) => (dispatch) => {
  fetchWithToken(`/api/v1/personal/users/${id}/employee_feedbacks`, dispatch, teamFeedbacks);
};

export const employeeFeedbacksData = (id) => (dispatch) => {
  fetchWithToken(`/api/v1/company/employees/${id}/feedbacks`, dispatch, employeeFeedbacks);
};

export const employeeFeedbacksDatalist = (enpid, feedid) => (dispatch) => {
  fetchWithToken(`/api/v1/company/employees/${enpid}/feedbacks?feedback_id=${feedid}`, dispatch, employeeFeedbackslist);
};

// Updated fetchData-based thunks to use specific actions
export const companySubmissionDetail = (id, userid) => fetchData(`api/v1/company/submissions/${id}?user_id=${userid}`, companySubmissionDetailData);

export const getSetupServicesData = (id) => fetchData(`/api/v1/setup_services/${id}`, SetServicesData);

export const getServicesData = () => (dispatch) => {
  fetchWithToken('/api/v1/company/services/company_services', dispatch, servicesDetailData);
};

export const getServicesSortedData = () => (dispatch) => {
  fetchWithToken('/api/v1/company/services/company_services', dispatch, servicesSortedDetailData);
};

export const getUsageRequestDataDetails = (id, reqid) => (dispatch) => {
  fetchWithToken(`api/v1/company/services/${id}/usage_report/requests?request_id=${reqid}`, dispatch, updateRequestDetails);
};

export const getEmployeeData = (userId) => (dispatch) => {
  fetchWithToken(`/api/v1/company/employees?user_id=${userId}`, dispatch, updateEmployees);
};

export const getEmployeeSortedData = (userId) => (dispatch) => {
  fetchWithToken(`/api/v1/company/employees?user_id=${userId}`, dispatch, updateSortedEmployees);
};

export const getSavingsOpportunities = () => (dispatch) => {
  fetchWithToken(`/api/v1/savings_opportunities`, dispatch, savingsOpportunities);
};

export const getManageServicesDataScore = (billing_period_id) => (dispatch) => {
  fetchWithToken(`/api/v1/manage_service_data_score?billing_period_id=${billing_period_id}`, dispatch, manageServicesDataScore);
};

export const notifySingleServiceOwner = (service_id) => (dispatch) => {
  postWithToken(`/api/v1/notify_service_owners/${service_id}`, {}, dispatch, notifyServiceOwner);
};

export const notifyMultipleServiceOwner = (service_id) => (dispatch) => {
  postWithToken(`/api/v1/notify_multiple_service_owners/${service_id}`, {}, dispatch, notifyMultiServiceOwner);
};

export const companyPublishReports = () => (dispatch) => {
  patchWithToken(`/api/v1/company_publish_report`, {}, dispatch, notifyPublishReport);
};

export const customerPublishReports = (billing_period) => (dispatch) => {
  patchWithToken(`/api/v1/customer_publish_report`, billing_period, dispatch, customerPublishedReport);
};

export const companyPublishMonthlyReports = (company_data) => (dispatch) => {
  patchWithToken(`/api/v1/company_publish_monthly_report`, company_data, dispatch, notifyPublishMonthlyReport);
};

export const updateSavingOpportunityStatus = (id) => (dispatch) => {
  patchWithToken(`/api/v1/savings_opportunities/${id}/update_opportunity_status`, {}, dispatch, updateOpportunityStatus);
};

export const getCompanyExpenses = (customBillingPeriodId = null) => (dispatch) => {
  fetchWithToken(`/api/v1/company_expenses?customBillingPeriodId=${customBillingPeriodId}`, dispatch, updateCompanyExpenses);
};

export const updateCompanyCurrency = (currency) => (dispatch) => {
  patchWithToken(`/api/v1/update_company_currency`, currency, dispatch, updateCurrency);
};

export const getBillingPeriods = () => (dispatch) => {
  fetchWithToken(`/api/v1/get_billing_periods`, dispatch, getBillingPeriodsData);
};

export const getMonthReports = () => (dispatch) => {
  fetchWithToken(`/api/v1/month_reports`, dispatch, getMonthReportsResult);
};

export const getOwnerAuthorizedServices = () => (dispatch) => {
  fetchWithToken(`/api/v1/owner_authorized_services`, dispatch, ownerAuthorizedServices);
};

export const updateCompanyAtiveBillingPeriod = (billing_period) => (dispatch) => {
  patchWithToken(`/api/v1/update_active_billing_period`, billing_period, dispatch, updateActiveBillingPeriod);
};

export const resetPasswordEmail = (user_id) => (dispatch) => {
  postWithToken(`/api/v1/company/employees/${user_id}/reset_password_email`, {}, dispatch, resetPassword);
};

export const createApprovalRule = (ruleData) => (dispatch) => {
  postWithToken(`/api/v1/approval_rules`, ruleData, dispatch, addApprovalRule);
};

export const getApprovalRules = () => (dispatch) => {
  fetchWithToken(`/api/v1/approval_rules`, dispatch, fetchApprovalRules);
};

export const updateApprovalFlow = (id, ruleData) => (dispatch) => {
  patchWithToken(`/api/v1/approval_rules/${id}`, ruleData, dispatch, updateApprovalRule);
};

export const deleteApprovalFlow = (id) => (dispatch) => {
  deleteWithToken(`/api/v1/approval_rules/${id}`, dispatch, deleteApprovalRule);
};

export const deleteApprovalStep = (id) => (dispatch) => {
  deleteWithToken(`/api/v1/approval_steps/${id}`, dispatch, destroyApprovalStep);
};

export const getComopanyNotifications = () => (dispatch) => {
  fetchWithToken("api/v1/company/notifications", dispatch, updateNotifications);
};

export const compMarkAllReadNotifications = (id) => (dispatch) => {
  postWithToken("api/v1/company/notifications/mark_all_read",{}, dispatch, markAllReadNotification);
};

export const getUsageserviceDataDetails = (id, userid) => fetchData(`api/v1/company/services/${id}/usage_report/service_users?s_user_id=${userid}`, usagecompanyReportData);
export const getCompanyUsageReportData = (id, empid, billing_period_id=null) => fetchData(`api/v1/services/${id}/employees/${empid}/usage_report?billing_period_id=${billing_period_id}`, usagecompanyReportData);
export const getCompanyPrintingUsageReportData = (id, empid) => fetchData(`api/v1/services/${id}/employees/${empid}/usage_report_printing`, companyPrintingReportData);
export const getCompanyServicesRequest = (id, empid) => fetchData(`api/v1/services/${id}/employees/${empid}/requests`, usageReportRequestData);
export const getCompanyServicesFeedback = (id, empid) => fetchData(`api/v1/services/${id}/employees/${empid}/feedbacks`, usageReportFeedbacksData);
export const getCompanyServiceCostHistory = (id, empid, billing_period_id=null) => fetchData(`api/v1/services/${id}/employees/${empid}/costs_history?billing_period_id=${billing_period_id}`, empServiceHistoryData);
export const getHomeData = () => fetchData('/api/v1/company_dashboard', homeData);
export const getEmployeeSpecficData = (id) => fetchData(`/api/v1/company/employees/${id}`, employeeData);


export const getSubmissionsData = (id) => fetchData(`api/v1/company/services/${id}/submissions`, submissionsData);
export const getServiceSubmissionsData = (id) => fetchData(`api/v1/company/services/${id}/single_service_submissions`, submissionsRecord);
export const getCompanyHistoryRequest = () => fetchData('api/v1/company/costs_history', CompanyhistoryData);
export const getUsageReportData = (id, customBillingPeriodId=null) => fetchData(`api/v1/company/services/${id}/usage_report?customBillingPeriodId=${customBillingPeriodId}`, usageReportData);
export const getPrintingUsageReportData = (id) => fetchData(`api/v1/company/services/${id}/usage_report_printing`, printingUsageReportData);
export const getTeamUsageReportData = (service_id, user_id) => fetchData(`/api/v1/personal/users/${service_id}/team_usage_report?user_id=${user_id}`, usageReportData);
export const getCompanySubmissionData = () => fetchData('/api/v1/company/services/service_submissions', SubmissionsListData);

export const getUsageFeedbackData = (id, billing_period_id=null) => fetchData(`api/v1/company/services/${id}/usage_report/feedbacks?billing_period_id=${billing_period_id}`, usageReportFeedbacksData);
export const getUsageSpecificFeedback = (id, feedBackId) => fetchData(`api/v1/company/services/${id}/usage_report/feedbacks?feedback_id=${feedBackId}`, specficFeedbackData);
export const getUsageRequestData = (id, billing_period_id=null) => fetchData(`api/v1/company/services/${id}/usage_report/requests?billing_period_id=${billing_period_id}`, usageReportRequestData);

export const getUsageServiceData = (id, billing_period_id=null) => fetchData(`api/v1/company/services/${id}/usage_report/service_users?billing_period_id=${billing_period_id}`, usageReportServiceData);
export const getUsageTeamServiceUserData = (service_id, user_id) => fetchData(`/api/v1/personal/users/${service_id}/usage_report_service_users?user_id=${user_id}`, usageReportServiceData);
export const getUsageTeamRequestData = (service_id, user_id) => fetchData(`/api/v1/personal/users/${service_id}/usage_report_requests?user_id=${user_id}`, usageReportRequestData);
export const getUsageTeamFeedbackData = (service_id, user_id) => fetchData(`/api/v1/personal/users/${service_id}/usage_report_feedbacks?user_id=${user_id}`, usageReportFeedbacksData);

export default companyDashboardSlice.reducer;
